import './GeneralConfig.scss';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Button, Col, Divider, Form, Input, Row, Select, Space, Tooltip } from 'antd';
import { DeleteOutlined, SaveOutlined, StarFilled } from '@ant-design/icons';
import { createWSUser, deleteWSUser, editWSUser, getGeneralMappingConfig, SaveGeneralMappingConfig, getWSUsers } from '../../../../../../api/repositoryEP';
import LoadingDataComponent from '../../../../../common/LoadingData/LoadingDataComponent';
import { useAppConfig } from '../../../../../../Contexts/AppConfigContext';
import { WSUsers } from '../../../../../../models/ConfigurationData';

interface GeneralConfigProps {
    language: string;
    countries: { key: string; value: string }[];
}

interface WSUserRow {
    id: string;
    username: string;
    password: string;
    market: string;
    isNew: boolean;
    isBase: boolean;
}

interface WSUserRowErrors {
    username?: boolean;
    password?: boolean;
    market?: boolean;
}

const GeneralConfig = (props: GeneralConfigProps): JSX.Element => {

    const { t } = useTranslation();
    const [loading, setLoading] = useState<boolean>(true);
    const [buttonLoading, setButtonLoading] = useState<boolean>(false);
    const [form] = Form.useForm();
    const [initialValue, setInitialValue] = useState<string>('');
    const appConfig = useAppConfig();
    const [wsUserRows, setWsUserRows] = useState<WSUserRow[]>([]);
    const [savingRows, setSavingRows] = useState<Record<string, boolean>>({});
    const [deletingRows, setDeletingRows] = useState<Record<string, boolean>>({});
    const [rowErrors, setRowErrors] = useState<Record<string, WSUserRowErrors>>({});

    const loadWSUsers = () => {
        getWSUsers().then((response) => {
            if (response?.type === 1) {
                const mappedRows: WSUserRow[] = (response.data || [])
                    .map((user: WSUsers, index: number) => ({
                        id: `${user.market || 'no-market'}-${index}`,
                        username: user.username || '',
                        password: user.password || '',
                        market: user.market || '',
                        isNew: false,
                        isBase: user.isBase ?? false
                    }))
                    .sort((a: any, b: any) => (b.isBase ? 1 : 0) - (a.isBase ? 1 : 0));

                setWsUserRows(mappedRows);
            }
        });
    };

    useEffect(() => {
        loadWSUsers();
    }, []);


    useEffect(() => {
        getGeneralMappingConfig().then((response) => {
            if (response?.type === 1) {
                const rawDirectCode = response.data && typeof response.data === 'object'
                    ? (response.data as { directIntegrationChannelCode?: unknown }).directIntegrationChannelCode
                    : undefined;

                const directCode = rawDirectCode === false || rawDirectCode === 'false' || rawDirectCode == null
                    ? ''
                    : String(rawDirectCode);

                setInitialValue(directCode);

                form.setFieldsValue({
                    directIntegrationChannelCode: directCode
                });
            }

            setLoading(false);
        });
    }, []);




    const onFinish = () => {
        setButtonLoading(true);
        SaveGeneralMappingConfig(form.getFieldValue('directIntegrationChannelCode')).then((response) => {
            if (response && response.type == 1 && response.data == true) { setInitialValue(form.getFieldValue('directIntegrationChannelCode')) }
            setButtonLoading(false);
        })
    }

    const updateRow = (rowId: string, field: 'username' | 'password' | 'market', value: string): void => {
        setWsUserRows((prevRows) => prevRows.map((row) => row.id === rowId ? { ...row, [field]: value } : row));
        setRowErrors((prev) => {
            const current = prev[rowId];
            if (!current || !current[field]) {
                return prev;
            }

            return {
                ...prev,
                [rowId]: {
                    ...current,
                    [field]: !value.trim()
                }
            };
        });
    };

    const addNewWSUserRow = (): void => {
        const id = `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        setWsUserRows((prevRows) => ([
            ...prevRows,
            { id, username: '', password: '', market: '', isNew: true, isBase: false }
        ]));
    };

    const onSaveRow = async (row: WSUserRow): Promise<void> => {
        const currentErrors: WSUserRowErrors = {
            username: !row.username.trim(),
            password: !row.password.trim(),
            market: row.isNew ? !row.market.trim() : false
        };

        if (currentErrors.username || currentErrors.password || currentErrors.market) {
            setRowErrors((prev) => ({ ...prev, [row.id]: currentErrors }));
            return;
        }

        setRowErrors((prev) => ({ ...prev, [row.id]: { username: false, password: false, market: false } }));

        setSavingRows((prev) => ({ ...prev, [row.id]: true }));

        try {
            const response = row.isNew
                ? await createWSUser(row.market, row.username.trim(), row.password.trim())
                : await editWSUser(row.market, row.username.trim(), row.password.trim());

            if (response?.type === 1) {
                setWsUserRows((prevRows) => prevRows.map((item) => item.id === row.id ? { ...item, isNew: false } : item));
                return;
            }
        } finally {
            setSavingRows((prev) => ({ ...prev, [row.id]: false }));
        }
    };

    const onDeleteRow = async (row: WSUserRow): Promise<void> => {
        if (row.isNew) {
            setWsUserRows((prevRows) => prevRows.filter((item) => item.id !== row.id));
            return;
        }

        // The principal WS user (without market) must never be deletable.
        if (!row.market) {
            return;
        }

        setDeletingRows((prev) => ({ ...prev, [row.id]: true }));

        try {
            const response = await deleteWSUser(row.market);

            if (response?.type === 1) {
                setWsUserRows((prevRows) => prevRows.filter((item) => item.id !== row.id));
                return;
            }
        } finally {
            setDeletingRows((prev) => ({ ...prev, [row.id]: false }));
        }
    };



    return (
        <div className="services-configuration-component">
            {loading && (<LoadingDataComponent />)}
            {!loading && (
                <>
                    <Form
                        layout="vertical"
                        onFinish={onFinish}
                        form={form}

                    >
                        <div className="ws-users-divider-wrap">
                            <Divider orientation="center">{t("components.tab-mapping.general-config.ws-users-title") || ''}</Divider>
                        </div>

                        <div className="ws-users-section">
                            {wsUserRows.map((row) => (
                                <Row gutter={[10, 10]} key={row.id} className="ws-user-row" align="top">
                                    <Col xs={24} md={12} className="ws-user-field-col">
                                        <Form.Item
                                            validateStatus={rowErrors[row.id]?.username ? 'error' : ''}
                                            help={rowErrors[row.id]?.username ? (t("components.tab-mapping.general-config.ws-user-required-field") || '') : ''}
                                        >
                                            <Input
                                                className='app-input ws-user-input'
                                                value={row.username}
                                                onChange={(event) => updateRow(row.id, 'username', event.target.value)}
                                                placeholder={t("components.tab-mapping.general-config.ws-user-username-placeholder") || ''}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={7} className="ws-user-field-col">
                                        <Form.Item
                                            validateStatus={rowErrors[row.id]?.password ? 'error' : ''}
                                            help={rowErrors[row.id]?.password ? (t("components.tab-mapping.general-config.ws-user-required-field") || '') : ''}
                                        >
                                            <Input.Password
                                                className='app-input ws-user-input'
                                                value={row.password}
                                                onChange={(event) => updateRow(row.id, 'password', event.target.value)}
                                                placeholder={t("components.tab-mapping.general-config.ws-user-password-placeholder") || ''}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={2} className="ws-user-field-col ws-user-market-col">
                                        {(() => {
                                            const usedMarkets = new Set(
                                                wsUserRows
                                                    .filter((item) => item.id !== row.id && !!item.market)
                                                    .map((item) => item.market)
                                            );

                                            const uniqueCountries = (props.countries || []).filter((country, index, array) =>
                                                array.findIndex((item) => item.key === country.key) === index
                                            );

                                            const availableOptions = uniqueCountries
                                                .filter((country) => !usedMarkets.has(country.key) || country.key === row.market)
                                                .map((country) => ({
                                                    value: country.key,
                                                    label: <span title={country.value}>{country.key}</span>
                                                }));

                                            const selectedCountryValue = uniqueCountries.find((country) => country.key === row.market)?.value || '';

                                            return (
                                                <Form.Item
                                                    validateStatus={rowErrors[row.id]?.market ? 'error' : ''}
                                                    help={rowErrors[row.id]?.market ? (t("components.tab-mapping.general-config.ws-user-required-field") || '') : ''}
                                                >
                                                    <Tooltip title={selectedCountryValue}>
                                                        <span className="ws-user-market-tooltip-trigger">
                                                            <Select
                                                                className='app-select ws-user-market-select'
                                                                value={row.market || undefined}
                                                                placeholder={row.isNew ? (t("components.tab-mapping.general-config.ws-user-market-placeholder") || '') : undefined}
                                                                disabled={!row.isNew}
                                                                showSearch
                                                                popupClassName="ws-user-market-dropdown"
                                                                optionFilterProp="value"
                                                                filterOption={(input, option) => (String(option?.value || '')).toLowerCase().includes(input.toLowerCase())}
                                                                onChange={(value) => updateRow(row.id, 'market', value)}
                                                                options={availableOptions}
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                </Form.Item>
                                            );
                                        })()}

                                    </Col>
                                    <Col xs={24} md={3} className="ws-user-field-col ws-user-actions-col">
                                        <div className="ws-user-actions-wrap">
                                            {row.isBase && (
                                                <Tooltip title={t("components.tab-mapping.general-config.ws-user-general-tooltip") || ''}>
                                                    <StarFilled className="ws-user-general-marker" />
                                                </Tooltip>
                                            )}
                                            <Space align="center" className="ws-user-actions">
                                                <Button
                                                    type='primary'
                                                    icon={<SaveOutlined />}
                                                    loading={!!savingRows[row.id]}
                                                    onClick={() => onSaveRow(row)}
                                                />
                                                <Button
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    disabled={row.isBase}
                                                    loading={!!deletingRows[row.id]}
                                                    onClick={() => onDeleteRow(row)}
                                                />
                                            </Space>
                                        </div>
                                    </Col>
                                </Row>
                            ))}

                            <Row className="add-ws-user-row">
                                <Col>
                                    <Button className='app-button' onClick={addNewWSUserRow}>{t("components.tab-mapping.general-config.ws-user-add-button") || ''}</Button>
                                </Col>
                            </Row>
                        </div>

                        {appConfig.config.hasRewardsContracted && (
                            <>
                                <Divider orientation="center">{t("components.tab-mapping.general-config.rewards-title") || ''}</Divider>

                                <Row gutter={[10, 10]} className="rewards-config-row">
                                    <Col xs={24} md={24} className="rewards-config-col">
                                        <Form.Item

                                            label={t("components.tab-mapping.general-config.direct-integration-code") || ''}
                                            name="directIntegrationChannelCode"
                                            rules={[
                                                { required: true, message: t("components.tab-mapping.general-config.direct-integration-code-required") || "" },
                                            ]}
                                        >
                                            <Input />
                                        </Form.Item>


                                        <Col xs={24} md={12} >
                                            <Form.Item shouldUpdate>
                                                {() => {
                                                    const currentValue = form.getFieldValue('directIntegrationChannelCode') || '';

                                                    const isSame = currentValue === initialValue;

                                                    return (
                                                        <Button
                                                            className='app-button'
                                                            htmlType='submit'
                                                            loading={buttonLoading}
                                                            disabled={
                                                                buttonLoading ||
                                                                isSame ||
                                                                form.getFieldsError().some(({ errors }) => errors.length)
                                                            }
                                                        >
                                                            {t("components.button.save") || ''}
                                                        </Button>
                                                    );
                                                }}
                                            </Form.Item>

                                        </Col>
                                    </Col>

                                </Row>
                            </>
                        )}

                    </Form>
                </>
            )}

        </div>
    );
};



export default GeneralConfig;